import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaLeaf, FaSeedling, FaTree, FaCheckCircle, FaChevronLeft, FaChevronRight, FaWind, FaThermometerHalf, FaVolumeUp, FaPalette, FaTools, FaExpandArrowsAlt } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { useRef, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import ImageLightbox from '../components/ImageLightbox';
import WhatsAppButton from '../components/WhatsAppButton';
import CatalogModal from '../components/CatalogModal';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';

const JardinsVerticais = () => {
  const [heroRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const naturalSwiperRef = useRef<SwiperType | null>(null);
  const preservadoSwiperRef = useRef<SwiperType | null>(null);
  const artificialSwiperRef = useRef<SwiperType | null>(null);
  const musgoSwiperRef = useRef<SwiperType | null>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ src: '', alt: '', images: [] as string[], currentIndex: 0 });

  // Catalog modal state
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);

  const openLightbox = (src: string, alt: string, images: string[], index: number) => {
    setLightboxImage({ src, alt, images, currentIndex: index });
    setLightboxOpen(true);
  };

  const handlePreviousImage = () => {
    const newIndex = lightboxImage.currentIndex > 0 ? lightboxImage.currentIndex - 1 : lightboxImage.images.length - 1;
    setLightboxImage({
      ...lightboxImage,
      src: lightboxImage.images[newIndex],
      alt: `Imagem ${newIndex + 1}`,
      currentIndex: newIndex
    });
  };

  const handleNextImage = () => {
    const newIndex = lightboxImage.currentIndex < lightboxImage.images.length - 1 ? lightboxImage.currentIndex + 1 : 0;
    setLightboxImage({
      ...lightboxImage,
      src: lightboxImage.images[newIndex],
      alt: `Imagem ${newIndex + 1}`,
      currentIndex: newIndex
    });
  };

  // Jardins Verticais Natural
  const naturalImages = [
    '/images/Jardins verticais/Natural/IMG_5601.jpg',
    '/images/Jardins verticais/Natural/IMG_5602.jpg',
    '/images/Jardins verticais/Natural/IMG_5603.jpg',
    '/images/Jardins verticais/Natural/IMG_5604.jpg',
    '/images/Jardins verticais/Natural/IMG_5752.jpg',
    '/images/Jardins verticais/Natural/IMG_5753.jpg',
  ];

  // Jardins Verticais Preservado
  const preservadoImages = [
    '/images/Jardins verticais/Preservado/71A00FE0-6EE9-43B7-9D92-1F1F586AA26A.jpg',
    '/images/Jardins verticais/Preservado/99C29824-2EF2-4734-A121-8D7F7E722EC8.jpg',
    '/images/Jardins verticais/Preservado/C2A93728-2A72-49F5-BF31-68351AF23BCB.jpg',
    '/images/Jardins verticais/Preservado/D32877A6-8378-42ED-BA9F-0AFEBE4B6DB2.jpg',
    '/images/Jardins verticais/Preservado/E301A1D3-3D45-42CF-8710-A2177FF994FC.jpg',
    '/images/Jardins verticais/Preservado/IMG_3285.JPG',
  ];

  // Jardins Verticais Artificial
  const artificialImages = [
    '/images/Jardins verticais/Artificial/223ffbb5-5c6a-476f-b61f-31fbc0a75c81.jpg',
    '/images/Jardins verticais/Artificial/9bcf509e-e804-4d6c-ba86-2f9c006e2bf1.jpg',
    '/images/Jardins verticais/Artificial/IMG_1516.jpg',
    '/images/Jardins verticais/Artificial/IMG_2648.jpg',
    '/images/Jardins verticais/Artificial/IMG_2649.jpg',
  ];

  // Jardim Vertical Musgo Moss
  const musgoImages = [
    '/images/Jardins verticais/Musgo moss/1ab911be-ae07-4b8d-83ad-b8d95876e00c.jpg',
    '/images/Jardins verticais/Musgo moss/c8d1063f-0c31-468e-a1a2-0887a55c7eae.jpg',
    '/images/Jardins verticais/Musgo moss/IMG_1405.jpg',
    '/images/Jardins verticais/Musgo moss/IMG_2122.jpg',
    '/images/Jardins verticais/Musgo moss/IMG_2123.jpg',
  ];

  const benefits = [
    { text: 'Melhora a qualidade do ar', icon: FaWind },
    { text: 'Reduz a temperatura ambiente', icon: FaThermometerHalf },
    { text: 'Isolamento acústico natural', icon: FaVolumeUp },
    { text: 'Estética moderna e sofisticada', icon: FaPalette },
    { text: 'Baixa manutenção', icon: FaTools },
    { text: 'Otimização de espaços', icon: FaExpandArrowsAlt },
  ];

  const CarouselNavButton = ({
    direction,
    onClick
  }: {
    direction: 'prev' | 'next';
    onClick: () => void
  }) => (
    <button
      onClick={onClick}
      className="hidden md:block absolute top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-natureza hover:text-white text-floresta p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
      style={{ [direction === 'prev' ? 'left' : 'right']: '-20px' }}
    >
      {direction === 'prev' ? <FaChevronLeft size={20} /> : <FaChevronRight size={20} />}
    </button>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section - Modernizado */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image com Parallax Effect */}
        <div className="absolute inset-0">
          <img
            src="/images/Jardins verticais/Preservado/99C29824-2EF2-4734-A121-8D7F7E722EC8.jpg"
            alt="Jardins Verticais Hero"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-floresta/90 via-floresta/70 to-natureza/80" />

          {/* Decorative Elements */}
          <motion.div
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-20 right-20 w-64 h-64 bg-argila/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              y: [0, 20, 0],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            className="absolute bottom-20 left-20 w-96 h-96 bg-conforto/20 rounded-full blur-3xl"
          />
        </div>

        {/* Content */}
        <motion.div
          ref={heroRef}
          initial={{ opacity: 0, y: 50 }}
          animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 1 }}
          className="container-custom relative z-10 text-center text-white"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={heroInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ duration: 0.8, delay: 0.3, type: "spring" }}
            className="inline-block mb-8"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-argila/30 blur-2xl rounded-full" />
              <FaLeaf className="text-7xl md:text-8xl text-argila relative z-10 drop-shadow-2xl" />
            </div>
          </motion.div>

          <motion.h1
            className="font-display mb-6 text-5xl md:text-7xl drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]"
            style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.9), -1px -1px 2px rgba(0,0,0,0.5)' }}
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Jardins <span className="text-argila italic">Verticais</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl max-w-3xl mx-auto font-light leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]"
            style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.9), -1px -1px 2px rgba(0,0,0,0.6)' }}
            initial={{ opacity: 0 }}
            animate={heroInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Transforme suas paredes em obras de arte vivas com nossos jardins verticais exclusivos
          </motion.p>

          <motion.div
            className="mt-10"
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            <a
              href="#contato"
              className="btn-primary text-lg px-8 py-4 hover:scale-105 inline-block"
            >
              Solicitar Orçamento
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Benefits Section - Redesenhado */}
      <section className="section-padding bg-gradient-to-br from-white to-conforto/30 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-pattern opacity-5" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-natureza font-semibold text-sm uppercase tracking-wider">
              Benefícios
            </span>
            <h2 className="text-floresta font-display mt-4 mb-6">
              Por que escolher <span className="text-natureza italic">jardins verticais</span>?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.text}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className="group relative bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-transparent hover:border-natureza/20"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-natureza/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-center gap-4">
                    <div className="bg-gradient-to-br from-natureza to-floresta p-3 rounded-xl text-white flex-shrink-0 shadow-md">
                      <Icon className="text-2xl" />
                    </div>
                    <span className="text-jardim font-medium text-base">{benefit.text}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Jardim Vertical Natural - Com Carousel */}
      <section className="section-padding bg-conforto/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-2xl text-white">
                <FaTree className="text-4xl" />
              </div>
              <div className="flex-1">
                <h2 className="text-floresta font-display text-3xl md:text-4xl lg:text-5xl">
                  Jardim Vertical <span className="text-natureza italic">Natural</span>
                </h2>
                <p className="text-jardim text-lg mt-2">
                  Plantas vivas que trazem a natureza para o seu ambiente
                </p>
              </div>
            </div>

            {/* Carousel */}
            <div className="relative mb-12">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => { naturalSwiperRef.current = swiper; }}
                className="!pb-12"
              >
                {naturalImages.map((img, index) => (
                  <SwiperSlide key={img}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group relative overflow-hidden rounded-2xl shadow-xl aspect-[3/4] cursor-pointer"
                      onClick={() => openLightbox(img, `Jardim Vertical Natural ${index + 1}`, naturalImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Jardim Vertical Natural ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-floresta/80 via-floresta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavButton
                direction="prev"
                onClick={() => naturalSwiperRef.current?.slidePrev()}
              />
              <CarouselNavButton
                direction="next"
                onClick={() => naturalSwiperRef.current?.slideNext()}
              />
            </div>

            {/* Features */}
            <div className="bg-gradient-to-br from-conforto/20 via-white to-natureza/5 p-8 md:p-10 rounded-3xl shadow-2xl border border-natureza/10">
              <h3 className="text-floresta font-display text-2xl md:text-3xl mb-6">Características</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Plantio em inovador sistema de manta hidropônica',
                  'Placas extremamente leves em plástico de alta densidade',
                  'Irrigação automatizada com opção de monitoramento remoto via Wi-fi',
                  'Não possui ponto hidráulico? Temos a solução com nosso reservatório com bomba',
                  'Design paisagístico personalizado para o seu projeto com plantas vivas selecionadas para ambientes internos e externos'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <FaCheckCircle className="text-natureza mt-1 flex-shrink-0" />
                    <span className="text-jardim">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Jardim Vertical Preservado */}
      <section className="section-padding bg-conforto/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-2xl text-white">
                <FaSeedling className="text-4xl" />
              </div>
              <div className="flex-1">
                <h2 className="text-floresta font-display text-3xl md:text-4xl lg:text-5xl">
                  Jardim Vertical <span className="text-natureza italic">Preservado</span>
                </h2>
                <p className="text-jardim text-lg mt-2">
                  Beleza natural sem necessidade de manutenção
                </p>
              </div>
            </div>

            {/* Carousel */}
            <div className="relative mb-12">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                autoplay={{ delay: 4500, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => { preservadoSwiperRef.current = swiper; }}
                className="!pb-12"
              >
                {preservadoImages.map((img, index) => (
                  <SwiperSlide key={img}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group relative overflow-hidden rounded-2xl shadow-xl aspect-[3/4] cursor-pointer"
                      onClick={() => openLightbox(img, `Jardim Vertical Preservado ${index + 1}`, preservadoImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Jardim Vertical Preservado ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-floresta/80 via-floresta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavButton
                direction="prev"
                onClick={() => preservadoSwiperRef.current?.slidePrev()}
              />
              <CarouselNavButton
                direction="next"
                onClick={() => preservadoSwiperRef.current?.slideNext()}
              />
            </div>

            {/* Features */}
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-natureza/10">
              <h3 className="text-floresta font-display text-2xl md:text-3xl mb-6">Características</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Plantas naturais submetidas a processo de preservação especial',
                  'Zero manutenção - não precisa de água, luz ou poda',
                  'Durabilidade de 7 a 10 anos mantendo aparência natural',
                  'Ideal para ambientes internos com pouca luminosidade',
                  'Toque macio e aparência 100% natural'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <FaCheckCircle className="text-natureza mt-1 flex-shrink-0" />
                    <span className="text-jardim">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Jardim Vertical Artificial */}
      <section className="section-padding bg-conforto/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-2xl text-white">
                <FaLeaf className="text-4xl" />
              </div>
              <div className="flex-1">
                <h2 className="text-floresta font-display text-3xl md:text-4xl lg:text-5xl">
                  Jardim Vertical <span className="text-natureza italic">Artificial</span>
                </h2>
                <p className="text-jardim text-lg mt-2">
                  Alta qualidade com aspecto realista e durabilidade excepcional
                </p>
              </div>
            </div>

            {/* Carousel */}
            <div className="relative mb-12">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => { artificialSwiperRef.current = swiper; }}
                className="!pb-12"
              >
                {artificialImages.map((img, index) => (
                  <SwiperSlide key={img}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group relative overflow-hidden rounded-2xl shadow-xl aspect-[3/4] cursor-pointer"
                      onClick={() => openLightbox(img, `Jardim Vertical Artificial ${index + 1}`, artificialImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Jardim Vertical Artificial ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-floresta/80 via-floresta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavButton
                direction="prev"
                onClick={() => artificialSwiperRef.current?.slidePrev()}
              />
              <CarouselNavButton
                direction="next"
                onClick={() => artificialSwiperRef.current?.slideNext()}
              />
            </div>

            {/* Features */}
            <div className="bg-gradient-to-br from-conforto/20 via-white to-natureza/5 p-8 md:p-10 rounded-3xl shadow-2xl border border-natureza/10">
              <h3 className="text-floresta font-display text-2xl md:text-3xl mb-6">Características</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Folhagens artificiais de altíssima qualidade e realismo',
                  'Resistente a intempéries - ideal para áreas externas',
                  'Manutenção mínima - apenas limpeza esporádica',
                  'Proteção UV anti-desbotamento',
                  'Custo-benefício excepcional'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <FaCheckCircle className="text-natureza mt-1 flex-shrink-0" />
                    <span className="text-jardim">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Jardim Vertical Musgo Moss */}
      <section className="section-padding bg-gradient-to-br from-conforto/30 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="bg-gradient-to-br from-natureza to-floresta p-4 rounded-2xl text-white">
                <FaSeedling className="text-4xl" />
              </div>
              <div className="flex-1">
                <h2 className="text-floresta font-display text-3xl md:text-4xl lg:text-5xl">
                  Jardim Vertical <span className="text-natureza italic">Musgo Moss</span>
                </h2>
                <p className="text-jardim text-lg mt-2">
                  Textura única e sofisticação natural para ambientes refinados
                </p>
              </div>
            </div>

            {/* Carousel */}
            <div className="relative mb-12">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                autoplay={{ delay: 5500, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => { musgoSwiperRef.current = swiper; }}
                className="!pb-12"
              >
                {musgoImages.map((img, index) => (
                  <SwiperSlide key={img}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group relative overflow-hidden rounded-2xl shadow-xl aspect-[3/4] cursor-pointer"
                      onClick={() => openLightbox(img, `Jardim Vertical Musgo ${index + 1}`, musgoImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Jardim Vertical Musgo ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-floresta/80 via-floresta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavButton
                direction="prev"
                onClick={() => musgoSwiperRef.current?.slidePrev()}
              />
              <CarouselNavButton
                direction="next"
                onClick={() => musgoSwiperRef.current?.slideNext()}
              />
            </div>

            {/* Features */}
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-natureza/10">
              <h3 className="text-floresta font-display text-2xl md:text-3xl mb-6">Características</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Musgo 100% natural preservado com textura macia e realista',
                  'Isolamento acústico natural - reduz ruídos em até 60%',
                  'Zero manutenção - não requer água, luz ou cuidados',
                  'Durabilidade superior a 10 anos',
                  'Variedade de cores e composições personalizadas',
                  'Ideal para escritórios, home offices e ambientes corporativos'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <FaCheckCircle className="text-natureza mt-1 flex-shrink-0" />
                    <span className="text-jardim">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Catalog CTA Section */}
      <section className="section-padding bg-gradient-to-br from-floresta via-natureza to-floresta text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-argila/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-conforto/10 rounded-full blur-3xl" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-block mb-6"
            >
              <FaLeaf className="text-6xl text-argila" />
            </motion.div>

            <h2 className="font-display text-3xl md:text-4xl mb-4">
              Conheça todos os nossos <span className="italic text-argila">jardins verticais</span>
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Acesse nosso catálogo completo e descubra a solução perfeita para seu projeto
            </p>

            <button
              onClick={() => setIsCatalogModalOpen(true)}
              className="btn-outline inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm border-white/50 hover:border-white text-white"
            >
              Ver Catálogo Completo
            </button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <Footer />

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        imageSrc={lightboxImage.src}
        imageAlt={lightboxImage.alt}
        onClose={() => setLightboxOpen(false)}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
        hasPrevious={lightboxImage.images.length > 1}
        hasNext={lightboxImage.images.length > 1}
      />

      {/* Catalog Modal */}
      <CatalogModal
        isOpen={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
      />

      {/* WhatsApp Button */}
      <WhatsAppButton />

      {/* Custom Swiper Styles */}
      <style>{`
        .swiper-pagination-bullet {
          background: #c9ab8f;
          opacity: 0.5;
        }
        .swiper-pagination-bullet-active {
          background: #01544c;
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default JardinsVerticais;
